import { Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import { integrationService } from "/app/src/services";
import { useCallback, useEffect, useState } from "react";
import HeroButton from "/app/src/components/HeroUi/Button";
import Box from "/app/src/components/generic/components/box";
import HeroTextarea from "/app/src/components/HeroUi/Textarea";
import { isJSON } from "/app/src/helpers";
import HeroSwitch from "/app/src/components/HeroUi/Switch";
import Summary from "../common/Summary";

/**
 * Component to display the settings for a single Data Pull Integration
 */
export default function Test({ integration }: { integration: Integration }) {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [isManual, setIsManual] = useState<boolean>(false);
  const [textValue, setTextValue] = useState<string>("");
  const [textError, setTextError] = useState<string | null>(null);

  useEffect(() => {
    // If the integration is a delete type, then automatic testing is not possible
    if (integration.type === "delete") {
      setIsManual(true);
    }
  }, [integration.type]);

  const toggleIsManual = useCallback(() => {
    // When switching to connection mode, clear any text error
    if (isManual) setTextError(null);
    setIsManual(!isManual);
  }, [isManual]);
  /**
   * Test the integration
   */
  const handleTest = useCallback(async () => {
    setIsTesting(true);
    setResult(null);
    setTextError(null);
    let body = {};
    if (isManual) {
      if (!isJSON(textValue)) {
        setTextError("Invalid JSON");
        setIsTesting(false);
        return;
      }
      body = JSON.parse(textValue);
    }

    const response = await integrationService.testIntegration(
      integration.id,
      body,
    );
    // New backend shape for test: { data: { message, summary }, status }
    setResult(response?.data || response);
    setIsTesting(false);
  }, [integration.id, isManual, textValue]);

  const handleTextChange = useCallback(
    (e) => {
      const val = e.target.value;
      setTextValue(val);
      if (textError) {
        if (isJSON(val)) setTextError(null);
      }
    },
    [textError],
  );

  return (
    <Box>
      <div className="flex">
        <div>
          <HeroSwitch
            className="ml-2"
            isSelected={!isManual}
            onValueChange={toggleIsManual}
            size="md"
            isDisabled={integration.type === "delete"}
          />
        </div>
        <h4>
          {isManual
            ? t("translation:test_using_json")
            : t("translation:test_using_connection")}
        </h4>
      </div>
      <HeroTextarea
        className="mt-4"
        value={textValue}
        onChange={handleTextChange}
        maxRows={20}
        isDisabled={!isManual}
        isInvalid={Boolean(textError)}
        errorMessage={textError || undefined}
      />
      <Row gutter={16}>
        <Col offset={16} span={4} />
        <Col span={4}>
          <HeroButton
            color="primary"
            fullWidth
            onClick={handleTest}
            isDisabled={isTesting}
            className="mt-4"
          >
            {t("translation:test")}
          </HeroButton>
        </Col>
      </Row>
      {result && <Summary result={result} title="Test Summary" />}
    </Box>
  );
}
